import { useState, useMemo, useEffect, useRef } from "react"
import { ScopedOrganizationEntity } from "@/models/scoped-organization-hierarchy"
import { Label } from "@/components/shadcn/ui/label"
import { OrganizationField as OrganizationFieldType } from "@/models/new/forms.types"
import { ResponseValue, OrganizationResponse } from "@/models/new/formResponse.types"

interface Props {
  field: OrganizationFieldType
  value?: ResponseValue
  organizations?: ScopedOrganizationEntity[]
  onChange: (fieldId: string, value: ResponseValue) => void
  isRequired?: boolean
}

const OrganizationField = (props: Props) => {
  const { field, value, onChange, isRequired = true } = props
  const organizationsLoaded = props.organizations !== undefined
  const organizations = props.organizations ?? []

  // State to track selections at each level
  const [selections, setSelections] = useState<Record<string, string>>({})
  const previousValueRef = useRef<string>("")

  const currentValue = value?.type === "organization" ? (value as OrganizationResponse).value : ""

  // Find the root organization (type === "organization") and its hierarchy definition
  const rootOrg = useMemo(() => {
    const root = organizations.find((org) => org.type === "organization")
    return root
  }, [organizations])

  // Get hierarchy levels from the root organization's definition
  const hierarchyLevels = useMemo(() => {
    if (!rootOrg?.hierarchyDefinition?.levels) {
      return []
    }
    return rootOrg.hierarchyDefinition.levels
  }, [rootOrg])

  // Find the selected organization and its path
  const selectedOrg = useMemo(() => {
    if (!currentValue) return null
    const selected = organizations.find((org) => org.id === currentValue)
    return selected
  }, [currentValue, organizations])

  // Initialize selections from field value
  useEffect(() => {
    if (!organizationsLoaded) return
    if (!selectedOrg || !organizations.length) {
      if (currentValue) previousValueRef.current = currentValue
      setSelections({})
      return
    }

    const newSelections: Record<string, string> = {}
    const path = [...selectedOrg.path]
    if (!path.includes(selectedOrg.id)) {
      path.push(selectedOrg.id)
    }

    // Add selections for each org in the path
    path.forEach((id) => {
      const org = organizations.find((o) => o.id === id)
      if (org && org.type !== "organization") {
        const index = hierarchyLevels.findIndex((level) => level.type === org.type)
        if (index !== -1) {
          newSelections[index] = org.id
        }
      }
    })

    setSelections(newSelections)
  }, [currentValue, selectedOrg, organizations, hierarchyLevels, organizationsLoaded])

  // Get available options for a specific level
  const getOptionsForLevel = (level: number) => {
    if (!hierarchyLevels[level]) return []
    const levelType = hierarchyLevels[level].type

    // Skip the root organization level
    if (levelType === "organization") return []

    if (level === 1) {
      const options = organizations.filter(
        (org) => org.type === levelType && org.parentId === rootOrg?.id,
      )

      return options
    }

    // For other levels, filter based on parent selection
    const parentLevel = level - 1
    const parentId = selections[parentLevel]

    if (!parentId) return []

    // Get all organizations at this level that have the selected parent
    return organizations.filter((org) => org.type === levelType && org.parentId === parentId)
  }

  const handleSelectionChange = (level: number, value: string) => {
    setSelections((prev) => {
      const newSelections = { ...prev }
      // Clear all selections after this level
      Object.keys(newSelections).forEach((l) => {
        if (Number(l) > level) {
          delete newSelections[l]
        }
      })
      newSelections[level] = value
      return newSelections
    })
  }

  // Effect to handle onChange with selections
  useEffect(() => {
    const getDeepestSelectedId = () => {
      const levels = Object.keys(selections)
        .map(Number)
        .sort((a, b) => b - a)
      return (
        levels
          .map((level) => selections[level])
          .find((id) =>
            organizations.some((org) => org.id === id && org.capabilities.selectable),
          ) ?? ""
      )
    }

    const deepestId = getDeepestSelectedId()

    // Only call onChange if the value has actually changed
    if (deepestId !== previousValueRef.current) {
      previousValueRef.current = deepestId
      if (onChange) {
        onChange(field.id, {
          fieldName: field.name,
          type: "organization",
          value: deepestId,
        })
      }
    }
  }, [selections, organizations, onChange, field.id, field.name])

  return (
    <div className="w-1/2 p-4">
      <div className="organization-selector">
        {hierarchyLevels.map((level, index) => {
          if (level.type === "organization") return null

          const options = getOptionsForLevel(index)
          const selectedValue = selections[index]

          return (
            <div key={index} className="mb-4">
              <div>
                <Label>{level.displayName}</Label>
                <select
                  id={`level-${index}`}
                  value={selectedValue || ""}
                  onChange={(e) => handleSelectionChange(index, e.target.value)}
                  className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                >
                  <option value="">Select {level.displayName}</option>
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}{option.isContextOnly ? " (context)" : ""}
                    </option>
                  ))}
                </select>
                {isRequired && !selectedValue && (
                  <p className="text-sm text-red-500 mt-1">This field is required</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OrganizationField
